<template>
    <div class="media-card" @click="$emit('click')" :class="{ 'selectable': selectable }">
        <!-- Image Media -->
        <div v-if="mediaType === 'IMAGE'" class="media-card-image">
            <img :src="getMediaUrl(media.filename)" :alt="media.originalFilename" />
        </div>

        <!-- Video Media -->
        <div v-else-if="mediaType === 'VIDEO'" class="media-card-video">
            <div class="video-thumbnail">
                <i class="fal fa-play-circle fa-3x"></i>
                <video :src="getMediaUrl(media.filename)" class="video-bg"></video>
            </div>
        </div>

        <!-- Document Media -->
        <div v-else class="media-card-document">
            <div class="document-icon-wrapper">
                <i :class="getDocumentIcon(media)" class="fa-4x"></i>
            </div>
        </div>

        <!-- Media Type Badge -->
        <div class="media-type-badge">
            <i :class="getMediaTypeIcon(mediaType)"></i>
        </div>

        <!-- File Size Badge -->
        <div class="file-size-badge" v-if="media.fileSize">
            {{ formatFileSize(media.fileSize) }}
        </div>

        <!-- Content Footer -->
        <div class="media-card-footer">
            <div class="media-filename" :title="media.originalFilename">
                {{ truncateFilename(media.originalFilename || media.filename) }}
            </div>
            <div class="media-meta" v-if="media.createdAt">
                <small class="text-muted">{{ formatDate(media.createdAt) }}</small>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'MediaCard',
        props: {
            media: {
                type: Object,
                required: true
            },
            selectable: {
                type: Boolean,
                default: true
            }
        },
        computed: {
            mediaType() {
                return this.media.mediaType || 'DOCUMENT';
            }
        },
        methods: {
            getMediaUrl(filename) {
                // Add cache-busting timestamp to force reload after file replacement
                const baseUrl = this.fileResourceUrl(filename);
                if (this.media.updatedAt) {
                    const timestamp = new Date(this.media.updatedAt).getTime();
                    return `${baseUrl}?v=${timestamp}`;
                }
                return baseUrl;
            },

            getMediaTypeIcon(type) {
                const icons = {
                    IMAGE: 'fal fa-image',
                    VIDEO: 'fal fa-video',
                    DOCUMENT: 'fal fa-file-pdf'
                };
                return icons[type] || 'fal fa-file';
            },

            getDocumentIcon(media) {
                const mimeType = media.mimeType || '';
                const filename = media.filename || '';

                if (mimeType.includes('pdf') || filename.endsWith('.pdf')) {
                    return 'fal fa-file-pdf text-danger';
                } else if (mimeType.includes('word') || filename.match(/\.(doc|docx)$/)) {
                    return 'fal fa-file-word text-primary';
                } else if (mimeType.includes('excel') || filename.match(/\.(xls|xlsx)$/)) {
                    return 'fal fa-file-excel text-success';
                } else if (mimeType.includes('powerpoint') || filename.match(/\.(ppt|pptx)$/)) {
                    return 'fal fa-file-powerpoint text-warning';
                }
                return 'fal fa-file-alt text-secondary';
            },

            formatFileSize(bytes) {
                if (!bytes) return '';
                if (bytes < 1024) return bytes + ' B';
                if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
            },

            formatDate(dateString) {
                if (!dateString) return '';
                const date = new Date(dateString);
                return date.toLocaleDateString();
            },

            truncateFilename(filename, maxLength = 30) {
                if (!filename || filename.length <= maxLength) return filename;
                const extension = filename.split('.').pop();
                const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
                const truncated = nameWithoutExt.substring(0, maxLength - extension.length - 4);
                return `${truncated}...${extension}`;
            }
        }
    }
</script>

<style lang="scss" scoped>
    .media-card {
        position: relative;
        background: #fff;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        overflow: hidden;
        transition: all 0.2s ease;
        cursor: default;

        &.selectable {
            cursor: pointer;

            &:hover {
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                transform: translateY(-2px);
            }
        }

        .media-card-image,
        .media-card-video,
        .media-card-document {
            height: 200px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f8f9fa;
            overflow: hidden;

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }

        .media-card-video {
            position: relative;

            .video-thumbnail {
                position: relative;
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;

                i {
                    position: relative;
                    z-index: 2;
                    color: white;
                    text-shadow: 0 2px 4px rgba(0,0,0,0.5);
                }

                .video-bg {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    opacity: 0.3;
                }
            }
        }

        .media-card-document {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        }

        .media-type-badge {
            position: absolute;
            top: 8px;
            left: 8px;
            background: rgba(0, 0, 0, 0.7);
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 0.75rem;
            z-index: 10;
        }

        .file-size-badge {
            position: absolute;
            top: 8px;
            right: 8px;
            background: rgba(0, 0, 0, 0.7);
            color: white;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 0.7rem;
            z-index: 10;
        }

        .media-card-footer {
            padding: 12px;
            background: white;

            .media-filename {
                font-weight: 500;
                font-size: 0.9rem;
                margin-bottom: 4px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .media-meta {
                font-size: 0.75rem;
            }
        }
    }
</style>
